/**
 * Onboarding Orgs Page
 *
 * Shows aggregate org funnel and per-org setup progress table.
 * Each org row shows checkmarks for 12 onboarding steps.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import FunnelChart from '../../../components/founder-dashboard/FunnelChart';
import DataTable from '../../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

// Ordered onboarding steps for the progress table
const ORG_STEPS = [
  { key: 'org_created', short: 'Org' },
  { key: 'email_verified', short: 'Email' },
  { key: 'first_login', short: 'Login' },
  { key: 'campus_added', short: 'Campus' },
  { key: 'first_group', short: 'Group' },
  { key: 'second_admin', short: 'Admin' },
  { key: 'members_invited', short: 'Members' },
  { key: 'join_codes', short: 'Codes' },
  { key: 'first_prayer', short: 'Prayer' },
  { key: 'first_chat', short: 'Chat' },
  { key: 'first_event', short: 'Event' },
  { key: 'first_announcement', short: 'Announce' },
];

// Human-readable labels for org setup steps
const ORG_STEP_LABELS = {
  org_created: 'Organization Created',
  email_verified: 'Email Verified',
  first_login: 'First Login',
  campus_added: 'Campus Added',
  first_group: 'First Group Created',
  second_admin: 'Second Admin Invited',
  members_invited: 'Members Invited',
  join_codes: 'Join Codes Active',
  first_prayer: 'First Prayer',
  first_chat: 'First Chat Message',
  first_event: 'First Event',
  first_announcement: 'First Announcement',
};

function StepIcon({ completed }) {
  return completed ? (
    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
  ) : (
    <XCircleIcon className="w-5 h-5 text-gray-300" />
  );
}

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2 min-w-[60px]">
        <div
          className={`h-2 rounded-full transition-all ${
            pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
    </div>
  );
}

export default function OnboardingOrgsPage() {
  const { currentEnv } = useEnvironment();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingOrgs();
      setData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[OnboardingOrgs] Error fetching data:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to load data'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Build funnel stages from aggregate data
  // API returns: { aggregate: { org_created: 8, campus_added: 6, ... }, orgs: [...] }
  const buildAggregateFunnel = () => {
    const aggregate = data?.aggregate || {};
    const stepOrder = ORG_STEPS.map(s => s.key);
    const maxCount = Math.max(aggregate[stepOrder[0]] || 0, 1);

    return stepOrder.map((key) => ({
      name: key,
      label: ORG_STEP_LABELS[key],
      count: aggregate[key] || 0,
      percentage: Math.round(((aggregate[key] || 0) / maxCount) * 100),
    }));
  };

  const handleOrgStepClick = (step) => {
    navigate(`/onboarding/funnels/orgs/${step}`);
  };

  const handleOrgRowClick = (row) => {
    const orgId = row.id || row.organization_id;
    if (orgId) {
      navigate(`/onboarding/orgs/${orgId}`);
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">
          Couldn't load org onboarding data
        </p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  const orgsList = data?.orgs || [];

  // Build table columns for DataTable
  const tableColumns = [
    {
      key: 'name',
      header: 'Organization',
      sortable: true,
      render: (val) => (
        <span className="font-medium text-gray-900">{val || 'Unknown'}</span>
      ),
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (val, row) => (
        <span className="text-gray-700">{(val || row.member_count || row.members || 0).toLocaleString()}</span>
      ),
    },
    {
      key: 'lastActivity',
      header: 'Last Active',
      sortable: true,
      render: (val) => {
        if (!val) return <span className="text-gray-400">Never</span>;
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const d = new Date(val);
        const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return <span className="text-green-600">Today</span>;
        if (diffDays === 1) return <span className="text-green-600">Yesterday</span>;
        if (diffDays < 7) return <span className="text-yellow-600">{diffDays}d ago</span>;
        if (diffDays < 30) return <span className="text-orange-600">{Math.floor(diffDays / 7)}w ago</span>;
        return <span className="text-red-600">{Math.floor(diffDays / 30)}mo ago</span>;
      },
    },
    {
      key: 'progress',
      header: 'Progress',
      sortable: true,
      render: (_val, row) => {
        const steps = row.steps || {};
        const completed = ORG_STEPS.filter((s) => steps[s.key]).length;
        return <ProgressBar completed={completed} total={ORG_STEPS.length} />;
      },
    },
    // One column per step with a check/x icon
    ...ORG_STEPS.map((step) => ({
      key: `step_${step.key}`,
      header: step.short,
      sortable: false,
      render: (_val, row) => {
        const steps = row.steps || {};
        return <StepIcon completed={!!steps[step.key]} />;
      },
    })),
  ];

  // Filter + inject progress value for sorting
  const tableData = orgsList
    .filter((org) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (org.name || '').toLowerCase().includes(q)
        || (org.city || '').toLowerCase().includes(q)
        || (org.state || '').toLowerCase().includes(q);
    })
    .map((org) => {
      const steps = org.steps || {};
      const completed = ORG_STEPS.filter((s) => steps[s.key]).length;
      const memberCount = org.memberCount ?? org.member_count ?? org.members ?? 0;
      return { ...org, progress: completed, memberCount };
    });

  return (
    <div className="space-y-6">
      {/* Refresh Indicator */}
      <div className="flex justify-end">
        <RefreshIndicator
          lastUpdated={lastUpdated}
          onRefresh={fetchData}
          loading={loading}
        />
      </div>

      {/* Per-Org Progress Table */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Per-Organization Progress
            {tableData.length > 0 && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({tableData.length})
              </span>
            )}
          </h3>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations..."
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] w-64"
          />
        </div>
        <DataTable
          data={tableData}
          columns={tableColumns}
          onRowClick={handleOrgRowClick}
          loading={loading}
          emptyMessage="No organizations found. Orgs will appear here as they sign up."
          defaultSortKey="memberCount"
          defaultSortOrder="desc"
        />
      </div>
    </div>
  );
}
